import { useEffect, useState } from "react";
import { Alert, AlertTitle, Typography } from "@mui/material";
import { useTranslation } from "i18n-fe";
import { findSilentStations } from "../util/stationMonitor";
import { formatElapsedLabel } from "../util/scribePresence";
import StationExceptionLine from "./StationExceptionLine";
import BannerList from "./BannerList";

// A plain ticking clock (same pattern as StuckStationBanner), so a station
// crosses the 2 minute line on screen without waiting for the next poll.
function useNow(intervalMs) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}

const CLOCK_TICK_MS = 5000;

// #3475 -- one banner naming every station that is assigned to a running ring
// and has not been heard from for 2 minutes: the station, its ring, how long it
// has been silent and the unsent-sheet count it reported at last contact
// (#3436 heartbeat health). This is the case #3427's stuck-station banner cannot
// see: a tablet that is offline, dead or closed uploads nothing. Banner only, by
// decision (Jaybill, 2026-10-17): nothing is sent to chat. It clears on the
// first read after the station heartbeats again. Rendered by the Station Monitor
// page and the dashboard strip through StationMonitorBanners.
export default function SilentStationBanner({ rows, compact = false }) {
  const { t } = useTranslation("scoreadmin");
  const now = useNow(CLOCK_TICK_MS);
  const silent = findSilentStations(rows, now);
  if (silent.length === 0) return null;
  if (compact) {
    const text = t("stationMonitor.silent.compact", {
      count: silent.length,
      time: formatElapsedLabel(Math.max(...silent.map((s) => s.silentMs))),
    });
    return <StationExceptionLine severity="error" variant="filled" testId="silent-station-banner" text={text} />;
  }

  return (
    <Alert severity="error" variant="filled" role="alert" data-testid="silent-station-banner" sx={{ mb: 2 }}>
      <AlertTitle>{t("stationMonitor.silent.title")}</AlertTitle>
      {/* #3682: at most 3 stations, then "and N more". */}
      <BannerList
        testId="silent-station-list"
        items={silent}
        renderItem={(s) => {
          const named = Boolean(s.stationLabel);
          const vars = { station: s.stationLabel, ring: s.ring, time: formatElapsedLabel(s.silentMs) };
          return (
            <Typography variant="body2" data-testid={`silent-station-${s.key}`}>
              {named ? t("stationMonitor.silent.line", vars) : t("stationMonitor.silent.lineUnnamed", vars)}
              {s.unsent !== null && ` ${t("stationMonitor.silent.unsent", { count: s.unsent })}`}
            </Typography>
          );
        }}
      />
      <Typography variant="body2" sx={{ mt: 0.5 }}>
        {t("stationMonitor.silent.hint")}
      </Typography>
    </Alert>
  );
}
